import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
} from 'recharts';
import { useMemo } from 'react';

interface ChartData {
  time: number;
  value: number;
}

/** Default date format YY/MM/DD for axis and tooltip */
function formatTimeYYMMDD(time: number): string {
  const d = new Date(time);
  if (isNaN(d.getTime())) return '';
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}/${mm}/${dd}`;
}

interface ChartProps {
  data: ChartData[];
  color?: string;
  height?: number;
  formatTime?: (time: number) => string;
}

export const Chart = ({
  data,
  color = '#3b82f6',
  height = 200,
  formatTime = formatTimeYYMMDD,
}: ChartProps) => {
  const gradientId = useMemo(
    () => `chart-gradient-${color.replace('#', '')}`,
    [color]
  );
  const formatValue = (v: number): string => {
    if (!Number.isFinite(v)) return '';
    return v.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  };

  const chartData = useMemo(() => {
    return data.map(item => ({
      time: formatTime(item.time),
      timeMs: item.time,
      value: item.value,
    }));
  }, [data, formatTime]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        暂无数据
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.2} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="4 4"
          stroke="#e5e7eb"
          vertical={false}
        />
        <XAxis
          dataKey="time"
          stroke="#6b7280"
          fontSize={12}
          tick={{ fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          stroke="#6b7280"
          fontSize={12}
          tick={{ fill: '#6b7280' }}
          domain={['auto', 'auto']}
          axisLine={false}
          tickLine={false}
          tickFormatter={formatValue}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'rgba(0, 122, 255, 0.18)',
            border: '1px solid rgba(0, 122, 255, 0.35)',
            borderRadius: '12px',
            padding: '10px 12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(12px)',
          }}
          labelStyle={{ color: '#111827', fontWeight: 600 }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const item = payload[0]?.payload as { time?: string; timeMs?: number; value?: number };
            const dateStr = item?.time ?? (item?.timeMs != null ? formatTimeYYMMDD(item.timeMs) : '');
            const valueStr = item?.value != null ? formatValue(item.value) : '';
            return (
              <div style={{
                backgroundColor: 'rgba(0, 122, 255, 0.18)',
                border: '1px solid rgba(0, 122, 255, 0.35)',
                borderRadius: '12px',
                padding: '10px 12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                backdropFilter: 'blur(12px)',
              }}>
                <p style={{ color: '#111827', fontWeight: 600, margin: 0, marginBottom: '6px' }}>{dateStr}</p>
                <p style={{ margin: 0, color: '#007AFF', fontWeight: 600 }}>{valueStr}</p>
              </div>
            );
          }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke="none"
          fill={`url(#${gradientId})`}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
